/**
 * Partnerships & Affiliate — 2 templates.
 *
 * Two brands putting their names on one product, and the terms of an affiliate programme —
 * the two arrangements where someone else's decisions attach directly to your label.
 */

export const PARTNERSHIP_TEMPLATES = [
  // ───────────────────────────────────────────────────────────────────────────────────────
  // 1. CO-BRANDING AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "co-branding-agreement",
    docId: "co-branding-agreement",
    title: "CO-BRANDING AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Two brands are putting both names on one product — a collaboration SKU, a limited edition, or an ingredient partnership where a second brand's component is called out on your pack. Sign it before artwork starts and long before a production slot is booked.",
      whyItMatters:
        "Co-branding looks like a marketing exercise and behaves like a manufacturing joint venture. One brand's co-packer runs the product, so the other brand's formula, specification and artwork end up in a facility it did not vet. One brand holds the retail vendor number, so the deductions, chargebacks and recall obligations land on that side whoever had the idea. Both brands hold approval rights over the same artwork against one booked production window. And when it ends, someone is holding finished goods and a committed packaging minimum for a product neither party can sell alone.",
      negotiateCarefully: [
        {
          point: "Name the manufacturer and the seller of record",
          detail:
            "Say in the document which party manufactures, at which facility, under whose specification, and which party holds the retail vendor relationship and invoices the customer. Those three answers determine who absorbs deductions, who runs a recall, and whose obligations attach to the pack.",
        },
        {
          point: "Split the margin after deductions, not before",
          detail:
            "In a channel where slotting, allowances, spoilage credits and chargebacks are normal, a split calculated on gross invoice quietly moves money to whichever party is not holding the vendor relationship. Define the split on what is actually collected, net of everything taken back, and require a statement showing the working.",
        },
        {
          point: "Deemed approval, with a deadline that matters",
          detail:
            "Two approval chains against one production slot is the single most common reason a collaboration misses its date. Give each side one named approver, one submission point, a short response deadline, and a stated consequence for silence, then map the deadlines onto the print and production timeline before you agree them.",
        },
        {
          point: "Confirm every claim and certification survives the combination",
          detail:
            "Certifications and dietary claims belong to the whole formulation and the producing facility, not to either ingredient. Get written confirmation before artwork is approved that each claim on the pack holds for the combined product at the site that will run it, and address allergens that one brand's product introduces into the other's.",
        },
        {
          point: "Decide who owns what is left over",
          detail:
            "Unsold finished goods and committed packaging are the real exposure at the end, because a collaboration SKU cannot be sold by either party alone. Name the owner of remaining inventory, cap the packaging commitment to a tested forecast, and agree the sell-off and disposal path before the first print run.",
        },
      ],
      redFlags: [
        "No allocation of recall responsibility, cost or insurance between the two brands.",
        "Dual approval rights with no response deadline against a booked production run.",
        "Silence on who owns unsold finished goods and committed packaging.",
        "A margin split calculated before deductions in a channel where deductions are normal.",
        "No confidentiality and quality flow-down to the co-packer running the collaboration.",
        "No agreement on what either brand may sell, or use, after the term ends.",
      ],
      timeToComplete: "60 minutes to fill · agree the approval calendar and the split basis before artwork starts",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Your product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[PARTNER NAME]", label: "Partner's legal name", example: "Northstar Confections, Inc." },
      { token: "[PARTNER STATE]", label: "Partner's state of formation", example: "Oregon" },
      { token: "[PARTNER ENTITY TYPE]", label: "Partner's entity type", example: "corporation" },
      { token: "[PARTNER ADDRESS]", label: "Partner's address", example: "801 Mill Ave, Portland, OR 97209" },
      { token: "[PARTNER BRAND NAME]", label: "Partner's brand name", example: "Northstar" },
      { token: "[COLLABORATION PRODUCT]", label: "What the collaboration product is", example: "a limited-edition 12 oz sparkling beverage using Northstar cocoa" },
      { token: "[COMPANY CONTRIBUTIONS]", label: "What you contribute", example: "base formulation, packaging design, co-packer relationship and retail distribution" },
      { token: "[PARTNER CONTRIBUTIONS]", label: "What the partner contributes", example: "the cocoa component, its marks, and social and email promotion" },
      { token: "[MANUFACTURING PARTY]", label: "Which party manufactures", example: "Brand" },
      { token: "[MANUFACTURING SITE]", label: "Facility that will run the product", example: "Brand's co-packer named in Schedule A" },
      { token: "[SELLER OF RECORD]", label: "Which party invoices customers and holds the vendor relationship", example: "Brand" },
      { token: "[MARK HIERARCHY]", label: "How the two marks appear on pack", example: "Acme as the primary mark, Northstar as a called-out ingredient endorsement" },
      { token: "[TERRITORY]", label: "Territory the product may be sold in", example: "the United States and Canada" },
      { token: "[CHANNELS COVERED]", label: "Channels the product may be sold in", example: "grocery, natural retail and both parties' own e-commerce sites" },
      { token: "[EXCLUSIVITY SCOPE]", label: "What each party may not do during the term", example: "neither party may launch a similar co-branded product with a direct competitor of the other in the same category during the Term" },
      { token: "[APPROVAL RESPONSE PERIOD]", label: "Time to respond to an approval request", example: "5 business days" },
      { token: "[APPROVAL ESCALATION PERIOD]", label: "Time to escalate an unresolved approval", example: "3 business days" },
      { token: "[SPECIFICATION REFERENCE]", label: "The signed specification for the product", example: "the specification attached as Schedule A, version and date to be stated" },
      { token: "[MINIMUM SHELF LIFE]", label: "Stated shelf life of the combined product", example: "the shorter of the two components' shelf lives, stated in Schedule A" },
      { token: "[PRODUCTION VOLUME]", label: "Agreed production volume", example: "a single run of 6,000 cases, with any second run agreed in writing" },
      { token: "[PACKAGING COMMITMENT]", label: "Committed packaging and who pays for it", example: "one label and carton minimum, cost shared equally, sized to the agreed run" },
      { token: "[SPLIT BASIS]", label: "What the split is calculated on", example: "Net Collected Revenue as defined in Section 9" },
      { token: "[SPLIT PERCENTAGES]", label: "How the split is divided", example: "60% to Brand and 40% to Partner after landed cost" },
      { token: "[COST SHARING]", label: "How shared costs are borne", example: "ingredients, packaging and co-packing charged to the product; each party bears its own marketing" },
      { token: "[REPORTING CADENCE]", label: "Reporting cadence", example: "monthly, within 15 days of month end" },
      { token: "[PAYMENT CYCLE]", label: "When the split is paid", example: "within 30 days of each statement" },
      { token: "[STATEMENT DISPUTE PERIOD]", label: "Window to dispute a statement", example: "60 days" },
      { token: "[AUDIT PERIOD]", label: "How far back records may be examined", example: "the prior 24 months" },
      { token: "[INVENTORY OWNER]", label: "Which party owns finished goods inventory", example: "Brand, as the manufacturing and selling party" },
      { token: "[RECALL LEAD PARTY]", label: "Which party leads a recall or withdrawal", example: "Brand, as the manufacturer of record" },
      { token: "[RECALL COST SPLIT]", label: "How recall costs are allocated", example: "borne by the party whose component or act caused the issue, and otherwise shared in the revenue split proportions" },
      { token: "[RECALL NOTICE]", label: "Time to notify the other party of a quality issue", example: "within 24 hours of becoming aware" },
      { token: "[GENERAL LIABILITY LIMIT]", label: "Commercial general liability limit", example: "$1,000,000 per occurrence" },
      { token: "[PRODUCT LIABILITY LIMIT]", label: "Product liability limit", example: "$2,000,000 aggregate" },
      { token: "[INSURANCE NOTICE PERIOD]", label: "Notice before insurance lapses or changes", example: "30 days" },
      { token: "[INITIAL TERM]", label: "Term of the collaboration", example: "one year from first shipment" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "one year, only by written agreement" },
      { token: "[NON-RENEWAL NOTICE]", label: "Notice of non-renewal", example: "60 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "15 days" },
      { token: "[CONVENIENCE TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "60 days, not effective before the agreed run has shipped" },
      { token: "[SELL-OFF PERIOD]", label: "Post-termination sell-off period", example: "120 days" },
      { token: "[POST TERM RIGHTS]", label: "What each party may use after the term", example: "neither party may use the joint artwork or the combined formulation without the other's written consent" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long production and lot records are kept", example: "shelf life plus 2 years" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "5" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Co-Branding Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"), owner of the [BRAND NAME] brand; and

  [PARTNER NAME], a [PARTNER STATE] [PARTNER ENTITY TYPE], with a principal place of business at
  [PARTNER ADDRESS] ("Partner"), owner of the [PARTNER BRAND NAME] brand.

Brand sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand. The parties wish to develop,
produce and sell a single co-branded product carrying both brands. The parties agree as follows.

1. THE COLLABORATION PRODUCT
1.1 The "Product" is [COLLABORATION PRODUCT], made to [SPECIFICATION REFERENCE]. The Product is
the only item this Agreement covers, and neither party may extend the collaboration to another
item, size, flavour or format without a written amendment.
1.2 The Product's stated shelf life is [MINIMUM SHELF LIFE]. The parties acknowledge that the
Product's shelf life is set by its shortest-lived component and that code dating, storage
conditions and rotation follow from that.
1.3 The agreed production volume is [PRODUCTION VOLUME].

2. ROLES AND CONTRIBUTIONS
2.1 Brand shall contribute [COMPANY CONTRIBUTIONS].
2.2 Partner shall contribute [PARTNER CONTRIBUTIONS].
2.3 Costs are borne as follows: [COST SHARING]. Neither party may incur a cost chargeable to the
other, or to the Product's economics, without the other's prior written approval.
2.4 Each party shall perform its contributions in a timely way against the joint timeline in
Schedule C, and shall notify the other promptly of anything that will delay a milestone on it.

3. MANUFACTURING
3.1 [MANUFACTURING PARTY] is the manufacturing party and shall arrange production of the Product
at [MANUFACTURING SITE]. The manufacturing party is the manufacturer of record and is named as
such on the pack, and the obligations that attach to that role sit with it.
3.2 The manufacturing party shall produce the Product to the specification, shall not change the
formulation, ingredients, supplier, packaging or manufacturing site without the other party's
prior written approval, and shall provide the other party with the production schedule, the
certificates of analysis and the release documentation for each run.
3.3 The manufacturing party shall procure that the facility running the Product is bound, in
writing, to confidentiality and quality obligations covering the other party's formula,
specification and artwork that are at least as protective as those in this Agreement, and that
those obligations run for the benefit of the party that is not the facility's customer.
3.4 The party whose component or formula is used by the other's facility may, on reasonable
notice, review the facility's certifications, allergen controls and sanitation programme relevant
to the Product, or receive a written summary of them.
3.5 The manufacturing party shall maintain lot coding and traceability for the Product that allows
a single run to be traced to its inputs and to the customers that received it, and shall retain
those records for [RECORD RETENTION PERIOD].

4. MARKS
4.1 Each party owns its own marks. Nothing in this Agreement transfers, assigns or dilutes either
party's ownership of its marks, and neither party acquires any right in the other's marks except
the limited licence in Section 4.2.
4.2 Each party grants the other a non-exclusive, non-transferable, royalty-free licence to use its
marks solely on and in connection with the Product, in [TERRITORY], during the Term, in the form
and manner that party approves under Section 6. The marks appear on pack as follows:
[MARK HIERARCHY].
4.3 Neither party shall register or apply to register the other's marks, any combination of the
two marks, or anything confusingly similar, anywhere; shall not use the other's marks in its own
corporate name, domain name or social media handle; and shall not challenge the other's ownership
of its marks. Goodwill from each party's use of the other's marks runs to the owner of those
marks.
4.4 Each licence ends when the Term ends, subject only to the sell-off right in Section 15.

5. JOINTLY CREATED MATERIAL
5.1 Artwork, packaging designs, photography, copy, campaign material and any formulation created
specifically for the Product by or for both parties ("Joint Material") is owned jointly by the
parties, except that each party's own marks, existing artwork, existing formulations and
pre-existing material remain that party's sole property and are licensed only as Section 4
provides.
5.2 Neither party may use, license, adapt or reproduce Joint Material outside the Product during
the Term, or at any time after the Term, except as [POST TERM RIGHTS] provides or with the other
party's written consent.
5.3 Each party shall ensure that its employees, agencies, designers and contractors assign to it
the rights needed for Section 5.1 to operate, and shall obtain any third-party licence needed for
material it contributes.

6. APPROVALS
6.1 Each party shall name one approver and one submission address in Schedule C. Approvals are
requested and given in writing to those contacts, and no other person's comments constitute an
approval.
6.2 The following require both parties' prior written approval: artwork and packaging in every
format; the claims, certifications, allergen statements and ingredient deck appearing on pack;
the specification and any change to it; press releases and launch communications; and any use of
the other party's marks.
6.3 A party shall approve or reject a submission, with reasons, within [APPROVAL RESPONSE PERIOD].
If it does not respond within that period, the submission is deemed approved for the purpose of
keeping the timeline, provided the submission was complete and was sent to the named contact.
6.4 Where a party rejects a submission, the parties shall confer and escalate the point to a
senior representative of each party if it is not resolved within [APPROVAL ESCALATION PERIOD]. A
party may not withhold approval of a resubmission for a reason it could have raised on the
original submission.
6.5 Approval of artwork is not approval of a claim. Claims are approved separately under Section
7.

7. CLAIMS, CERTIFICATIONS AND ALLERGENS
7.1 Before artwork is approved, each party shall confirm in writing that every claim,
certification and dietary statement it wishes to appear on the pack holds for the combined Product
as made at the manufacturing site, and shall provide the supporting certificates and
documentation.
7.2 The parties acknowledge that a certification or dietary claim held by one party's own product
does not carry over to the Product automatically, and that the combined formulation and the
producing facility both bear on whether a claim can appear at all. Each party shall have its own
counsel and technical resources review the claims it asks to appear.
7.3 Each party shall disclose in writing every allergen present in the component it contributes,
and shall notify the other immediately of any change. Neither party shall approve artwork until
the combined allergen statement has been agreed in writing.
7.4 Neither party shall make any claim about the Product in marketing, on social media or to a
customer beyond the claims that appear on the approved pack and in material both parties have
approved.

8. CHANNELS, TERRITORY AND EXCLUSIVITY
8.1 The Product may be sold only in [TERRITORY] and only in [CHANNELS COVERED]. Neither party may
sell or authorise the sale of the Product outside that scope without the other's written consent.
8.2 [SELLER OF RECORD] is the seller of record. It holds the retail vendor relationship, issues
the invoices, collects the receivable, and bears the credit risk, and it shall keep the other
party informed of the customers, the terms and the deduction activity relating to the Product.
8.3 During the Term, [EXCLUSIVITY SCOPE].
8.4 Each party may continue to make, market and sell its own products, including products in the
same category as the Product, and nothing in this Agreement restricts a party's own line except
as Section 8.3 states.

9. ECONOMICS, REPORTING AND PAYMENT
9.1 The Product's economics are divided on the basis of [SPLIT BASIS], allocated
[SPLIT PERCENTAGES], as set out in Schedule B.
9.2 "Net Collected Revenue" means the amount the seller of record actually collects for the
Product, less: every allowance, discount, rebate, promotional fund, slotting or listing fee,
freight allowance, spoilage or unsaleable credit, chargeback and deduction taken by or credited
to the customer, whether at invoice or afterwards; returns and refunds; freight, fuel surcharges,
pallet charges and taxes; and product supplied as free fill, samples or demonstration stock.
9.3 The parties acknowledge that deductions and allowances in this channel are normal and
significant, that they fall in the first instance on the seller of record, and that Section 9.2 is
intended to share them rather than leave them with one party.
9.4 The seller of record shall provide a statement [REPORTING CADENCE], showing by customer and by
period the units shipped, the amounts invoiced, the deductions and allowances applied, the landed
cost, the Net Collected Revenue and each party's share. Payment follows [PAYMENT CYCLE].
9.5 A party may dispute a statement within [STATEMENT DISPUTE PERIOD], failing which it is final.
On reasonable notice and not more than once in a year, a party may have an independent accountant
examine the other's records relating to the Product for [AUDIT PERIOD] solely to verify the
statements, at the requesting party's cost unless the examination shows an understatement the
other party does not dispute.

10. INVENTORY AND COMMITTED PACKAGING
10.1 [INVENTORY OWNER] owns the finished goods inventory of the Product and carries it on its
books. Inventory risk, storage cost and obsolescence follow that ownership except as this Section
provides.
10.2 Committed packaging for the Product is [PACKAGING COMMITMENT]. Neither party may order
packaging beyond that commitment, or place a print order for a quantity above the agreed
production volume, without the other's prior written approval.
10.3 On termination or expiry, unsold finished goods and unused committed packaging are dealt with
under Section 15. Neither party may sell, transfer, donate, liquidate or destroy Product inventory
or committed packaging without the other's written consent.

11. QUALITY, HOLDS AND RECALL
11.1 Each party shall notify the other [RECALL NOTICE] of any complaint, test result, deviation,
adverse report, certification change or regulatory contact relating to the Product or to a
component it contributed.
11.2 [RECALL LEAD PARTY] leads any hold, withdrawal or recall of the Product and shall keep the
other party informed and involved throughout. Neither party shall make a public statement about a
Product quality issue without the other's prior consent, which shall not be unreasonably withheld
or delayed.
11.3 The costs of a hold, withdrawal or recall, including retrieval, destruction, customer credits,
notification and reasonable professional fees, are allocated as follows: [RECALL COST SPLIT].
11.4 Each party shall preserve samples, records, lot data and correspondence relating to an
incident, and shall cooperate with the other's investigation.

12. INSURANCE
12.1 Each party shall maintain commercial general liability insurance of at least
[GENERAL LIABILITY LIMIT] and product liability and products-completed operations coverage of at
least [PRODUCT LIABILITY LIMIT], covering the Product, together with any coverage its operations
require.
12.2 Each party shall name the other as an additional insured with respect to the Product, provide
certificates on request, and give at least [INSURANCE NOTICE PERIOD] written notice before a
policy is cancelled, is not renewed or is materially reduced. Insurance does not limit either
party's obligations under this Agreement.

13. INDEMNITY
13.1 Each party shall defend and indemnify the other and its officers, employees and agents
against third-party claims, and the resulting losses, damages, costs and reasonable legal fees,
arising from: the component, formula, artwork, marks or material that party contributed; that
party's breach of this Agreement; that party's negligence or wilful misconduct; and any claim or
statement about the Product that party made outside the approvals in Sections 6 and 7.
13.2 The manufacturing party shall in addition indemnify the other party against claims arising
from the manufacture, handling, packing, coding and release of the Product, except to the extent
caused by the other party's component or instructions.
13.3 The party seeking indemnity shall give prompt written notice, allow the indemnifying party to
control the defence with counsel it selects, and cooperate at that party's expense. No settlement
imposing an obligation or admission on the other party may be made without its written consent.

14. CONFIDENTIALITY
14.1 Each party shall keep confidential the other's non-public information, including formulas,
specifications, ingredient identities and sources, costs, margins, pricing, supplier and customer
identities, forecasts and deduction history; shall use it only for the Product; and shall not
disclose it without the other's written consent. These obligations continue for
[CONFIDENTIALITY YEARS] years after the Term and, for information that constitutes a trade
secret, for as long as it remains one.
14.2 A party may disclose the other's confidential information to the manufacturing site and to
professional advisers who need it for the Product and who are bound by obligations at least as
protective as these, and remains responsible for their compliance.

15. TERM, TERMINATION AND SELL-OFF
15.1 This Agreement begins on the Effective Date and continues for [INITIAL TERM]. It renews for
[RENEWAL TERM] only if both parties agree in writing, and a party intending not to renew shall
give [NON-RENEWAL NOTICE] written notice as a courtesy.
15.2 Either party may terminate on written notice if the other materially breaches and fails to
cure within [CURE PERIOD] after written notice describing the breach, and immediately if the other
becomes insolvent, makes an assignment for the benefit of creditors or ceases to carry on
business. Either party may terminate immediately if a claim or certification on the pack ceases to
hold for the Product, or on a Product safety issue that cannot be resolved.
15.3 Either party may terminate for convenience on [CONVENIENCE TERMINATION NOTICE].
15.4 On termination or expiry, the seller of record may sell the remaining finished goods
inventory of the Product in [CHANNELS COVERED] within [TERRITORY] for [SELL-OFF PERIOD], and both
parties' licences under Section 4 continue for that period for that purpose only. Neither party
may sell remaining Product to a liquidator, discounter or channel outside Section 8.1 without the
other's written consent.
15.5 At the end of the Sell-Off Period, all licences under Section 4 end absolutely, each party
shall cease all use of the other's marks, and remaining Product and committed packaging are dealt
with as the parties agree in writing or, failing agreement, by the owner under Section 10.1 at its
own cost with the other party's consent to disposal not to be unreasonably withheld.
15.6 After the Term, each party's rights are [POST TERM RIGHTS].
15.7 On termination each party shall return or destroy the other's confidential material,
specifications, artwork files and samples, except one archival copy retained subject to Section
14.
15.8 Sections 4.1, 4.3, 5, 9.5, 10.3, 11, 12, 13, 14, 15.4 through 15.8 and 16 survive
termination.

16. GENERAL
16.1 The parties are independent contractors. Nothing in this Agreement creates a partnership,
joint venture, agency or employment relationship, and neither party may bind the other or incur
an obligation in the other's name.
16.2 Neither party may assign this Agreement without the other's prior written consent, except to
a successor in a merger or a sale of substantially all of its assets or of the brand it
contributes, in which case it shall give the other written notice. A change of control of either
party is deemed an assignment.
16.3 Neither party is liable for a failure to perform caused by an event beyond its reasonable
control, provided it notifies the other promptly and works to resume performance.
16.4 Notices must be in writing and are effective on delivery to the addresses above. This
Agreement, with its Schedules, is the entire agreement between the parties on its subject matter
and supersedes all prior discussions and term sheets, and may be amended only in a writing signed
by both parties. A failure to enforce a provision is not a waiver of it, and if any provision is
held unenforceable the remainder continues in effect. This Agreement may be signed in
counterparts, including by electronic signature.
16.5 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [PARTNER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — THE PRODUCT
─────────────────────────────────────────────────────────────────────────────

Product name as it appears on pack .......... __________________________________
Format, size and case pack .................. __________________________________
Specification reference and version ......... __________________________________
Formula owner for each component ............ __________________________________
Manufacturing site .......................... __________________________________
Stated shelf life and its limiting component  __________________________________
Storage conditions and rotation standard .... __________________________________
Allergens present, by contributing party .... __________________________________
Certifications and dietary claims on pack ... __________________________________
Evidence each claim holds for the combined product ______________________________
Lot coding convention ....................... __________________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — ECONOMICS
─────────────────────────────────────────────────────────────────────────────

Set out the landed cost build for the Product, the price to the trade, the split basis and the
percentages, which costs are charged to the Product and which each party bears itself, the
treatment of deductions and allowances, the statement format, and a worked example for one case
showing what each party receives after deductions.


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — APPROVALS AND TIMELINE
─────────────────────────────────────────────────────────────────────────────

Brand's named approver and submission address  ___________________________________
Partner's named approver and submission address __________________________________
Artwork submission date ..................... __________________________________
Artwork approval deadline ................... __________________________________
Claims and certification confirmation deadline __________________________________
Packaging print order date and minimum ...... __________________________________
Production slot booked for .................. __________________________________
First ship date ............................. __________________________________
Launch and promotional dates ................ __________________________________
`,
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // 2. AFFILIATE AGREEMENT
  // ───────────────────────────────────────────────────────────────────────────────────────
  {
    id: "affiliate-agreement",
    docId: "affiliate-agreement",
    title: "AFFILIATE AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before you open an affiliate programme, install a tracking platform or hand out your first commission code. Also before you join a network, because the network's terms and your own programme terms have to agree with each other.",
      whyItMatters:
        "Affiliate programmes are sold as risk-free because you pay on results, and on a low-basket consumable that framing is misleading. Stack a commission on top of a coupon code, a first-order discount, free shipping and the parcel cost, and a single-unit order can cost more than it earns before the customer has tried the product once. The second exposure is what affiliates say to sell. A claim made by someone earning your commission attaches to your brand, and what may be said about a food, beverage or supplement is a question for counsel rather than for a publisher chasing conversion.",
      negotiateCarefully: [
        {
          point: "Set the base and the rate against a modelled order",
          detail:
            "Calculate the fully loaded cost of a representative single-unit order — commission, discount, shipping, packaging, payment fees — before you pick a number. Then define the base net of discounts, shipping and taxes so the percentage applies to what you actually keep.",
        },
        {
          point: "Codes are unique, and they do not stack",
          detail:
            "Issue one code per affiliate so attribution and abuse are traceable, and say plainly that a commission code cannot be combined with any other active promotion. Stacking on a low-priced consumable is the specific mechanism that takes an order below cost.",
        },
        {
          point: "Attribution window and coupon traffic",
          detail:
            "A long last-click window pays commission on customers already coming to you from email, search or a podcast. Keep the window short, and consider a lower rate or an exclusion for coupon and deal sites that capture checkout intent rather than create demand.",
        },
        {
          point: "An approved claims list is not optional",
          detail:
            "Attach a written list of the claims affiliates may make, have counsel set it for anything functional or supplement-adjacent, and prohibit everything outside it — including any suggestion the product is suitable for children, pregnancy or a medical condition.",
        },
        {
          point: "Hold, claw back, and switch the code off",
          detail:
            "Hold commission for a period matched to your refund window, reverse automatically on refunds and chargebacks, and make termination immediate with codes and links deactivated the same day. A live code after termination keeps paying someone you have already removed.",
        },
      ],
      redFlags: [
        "No clawback on refunded, cancelled or charged-back orders.",
        "Commission calculated on gross order value before discounts and shipping.",
        "No restriction on bidding against your own brand terms in paid search.",
        "Codes that stack with any other promotion by default.",
        "No claims restrictions and no approved-claims list for a supplement or functional product.",
        "Silence on marketplace listings and on resale of product bought at a discount.",
      ],
      timeToComplete: "40 minutes to fill · model a single-unit order and get the claims list from counsel first",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[AFFILIATE NAME]", label: "Affiliate's legal name", example: "Jordan Reyes Media LLC" },
      { token: "[AFFILIATE ADDRESS]", label: "Affiliate's address", example: "88 Cedar St, Denver, CO 80202" },
      { token: "[AFFILIATE CHANNELS]", label: "Channels the affiliate is approved to promote on", example: "one newsletter and two named social accounts" },
      { token: "[ENROLMENT APPROVAL PERIOD]", label: "Time to approve or decline an application", example: "10 business days" },
      { token: "[TRACKING PLATFORM]", label: "The tracking platform of record", example: "the affiliate platform named in the Program Policies" },
      { token: "[ATTRIBUTION MODEL]", label: "Attribution model", example: "last click, with a coupon code overriding a click" },
      { token: "[ATTRIBUTION WINDOW]", label: "Attribution window", example: "7 days from the tracked click" },
      { token: "[COMMISSION RATE]", label: "Commission rate", example: "10% of the Commission Base" },
      { token: "[COUPON TRAFFIC RATE]", label: "Rate for coupon and deal traffic", example: "3% of the Commission Base" },
      { token: "[SUBSCRIPTION COMMISSION RULE]", label: "How subscription orders are commissioned", example: "the first order only, with no commission on later cycles" },
      { token: "[COMMISSION EXCLUSIONS]", label: "Amounts excluded from the commission base", example: "shipping, taxes, deposits, gift cards, gift wrap and payment fees" },
      { token: "[EXCLUDED PRODUCTS]", label: "Products that carry no commission", example: "sample packs, bundles sold below cost and clearance items" },
      { token: "[CODE DISCOUNT]", label: "Discount an affiliate code carries", example: "15% off the first order" },
      { token: "[STACKING RULE]", label: "Whether codes may combine with other offers", example: "no stacking with any other code, site-wide promotion, first-order discount or free shipping offer" },
      { token: "[HOLDING PERIOD]", label: "How long commission is held before payment", example: "45 days from the order date, matching the refund window" },
      { token: "[PAYMENT CYCLE]", label: "When held commission is paid", example: "monthly, by the fifteenth of the following month" },
      { token: "[MINIMUM PAYOUT]", label: "Minimum payout threshold", example: "$50, with balances below it carried forward" },
      { token: "[REVERSAL WINDOW]", label: "Window for reversing commission", example: "120 days after the order date" },
      { token: "[STATEMENT DISPUTE PERIOD]", label: "Window to dispute a commission statement", example: "30 days" },
      { token: "[APPROVED CLAIMS LIST]", label: "The approved claims list", example: "the list attached as Exhibit A, reviewed by Brand's counsel" },
      { token: "[ASSET KIT]", label: "The approved asset kit", example: "the images, logos, copy and links in the affiliate portal" },
      { token: "[SHIPPING TERRITORY]", label: "Where orders can be fulfilled", example: "the United States, excluding territories" },
      { token: "[PAID SEARCH RULE]", label: "Paid search rule", example: "no paid search of any kind without Brand's prior written approval" },
      { token: "[PROGRAM CHANGE NOTICE]", label: "Notice before programme terms change", example: "15 days" },
      { token: "[TERMINATION NOTICE]", label: "Notice to terminate for convenience", example: "10 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a curable breach", example: "5 business days" },
      { token: "[ASSET REMOVAL PERIOD]", label: "Time to remove brand assets on termination", example: "3 business days" },
      { token: "[RECORD RETENTION PERIOD]", label: "How long records are kept", example: "2 years" },
      { token: "[CONFIDENTIALITY YEARS]", label: "Years confidentiality survives", example: "2" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Affiliate Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business at
  [COMPANY ADDRESS] ("Brand"); and

  [AFFILIATE NAME], of [AFFILIATE ADDRESS] ("Affiliate").

Brand sells [PRODUCT CATEGORY] products under the [BRAND NAME] brand (the "Products") and operates
an affiliate programme (the "Program"). Affiliate wishes to promote the Products for commission.
The parties agree as follows.

1. ENROLMENT AND APPROVAL
1.1 Affiliate has applied to the Program and shall provide the channels, audience information,
tax details and payment details Brand requires. Brand shall approve or decline an application
within [ENROLMENT APPROVAL PERIOD], and may decline for any reason.
1.2 Affiliate is approved to promote the Products on [AFFILIATE CHANNELS] only. Promotion on any
other site, account, list, channel or platform requires Brand's prior written approval, and
commission is not payable on traffic from an unapproved channel.
1.3 Affiliate participates as an independent contractor. Affiliate is not Brand's employee, agent,
partner or joint venturer, has no authority to bind Brand, and shall not represent otherwise.
Affiliate is responsible for its own taxes and for any personnel it engages.
1.4 Brand may publish written programme policies covering creative, codes, channels, disclosures
and approved claims (the "Program Policies"). The Program Policies form part of this Agreement.
Brand may change them, and may change the commission rate and the attribution window, on
[PROGRAM CHANGE NOTICE] written notice. A change applies to orders placed after it takes effect
and does not reduce commission already earned.

2. TRACKING AND ATTRIBUTION
2.1 Tracking is performed by [TRACKING PLATFORM], whose records are the record of orders,
attribution, refunds and commission for the Program. Where Affiliate's own analytics disagree with
those records, the platform's records govern.
2.2 Attribution is [ATTRIBUTION MODEL] with an attribution window of [ATTRIBUTION WINDOW]. An
order placed outside the window carries no commission.
2.3 Affiliate shall use only the tracking links and codes Brand issues to it. Affiliate shall not
alter, mask, cloak, redirect, iframe or rewrite a tracking link, shall not set or overwrite
tracking parameters other than by an issued link, and shall not use any method that attributes an
order Affiliate did not generate.
2.4 Brand may correct attribution and reverse commission where an order was attributed in breach
of this Section or of Section 5.

3. COMMISSION AND QUALIFYING SALES
3.1 Brand shall pay Affiliate [COMMISSION RATE] on the Commission Base for each Qualifying Sale.
3.2 A "Qualifying Sale" is an order of the Products that: is placed through Affiliate's tracking
link within the attribution window or with Affiliate's issued code; is shipped to an address in
[SHIPPING TERRITORY]; is paid for in full in cleared funds; and is not refunded, cancelled,
charged back or returned within the Holding Period.
3.3 The "Commission Base" is the order value after every discount, coupon, credit and promotion
applied to the order, less [COMMISSION EXCLUSIONS]. Commission is not calculated on the value
before discount.
3.4 [EXCLUDED PRODUCTS] carry no commission. Orders that Brand identifies as fraudulent, as
placed by Affiliate or a member of Affiliate's household, or as placed for resale, carry no
commission.
3.5 Traffic from coupon, deal, cashback and voucher sites is commissioned at
[COUPON TRAFFIC RATE].
3.6 Subscription and recurring orders are commissioned as follows:
[SUBSCRIPTION COMMISSION RULE].

4. CODES AND STACKING
4.1 Brand issues Affiliate a unique code. Affiliate shall not share, sell, transfer or allow
another person to use its code, and shall not use another affiliate's code.
4.2 An Affiliate code carries [CODE DISCOUNT] and is subject to the following stacking rule:
[STACKING RULE]. Affiliate shall not instruct, encourage or show customers how to combine its
code with another offer.
4.3 Affiliate shall not post, submit or permit the posting of its code to a coupon aggregator,
deal site, browser extension, forum or code-sharing service without Brand's prior written
approval, and shall use commercially reasonable efforts to have an unauthorised posting removed
on Brand's request.
4.4 Brand may deactivate a code at any time, and shall do so on termination under Section 11.

5. PROHIBITED MARKETING
5.1 Affiliate shall not, in promoting the Products:
(a) bid on, purchase or use in paid search any of Brand's trademarks, brand terms, product names,
    close variants, misspellings or combinations of them with generic words, and shall add them as
    negative keywords in any paid search campaign it runs; [PAID SEARCH RULE] applies to all other
    paid search;
(b) direct-link a paid advertisement to Brand's website, or run a paid advertisement that appears
    to originate from Brand;
(c) register, own or use a domain name, subdomain, app name, social handle or page name containing
    Brand's marks or a close variant, or operate a site that imitates Brand's site;
(d) list, offer or sell the Products, or any product bought with an Affiliate discount, on any
    marketplace, auction site, resale platform or retail channel, or otherwise resell the
    Products;
(e) buy the Products for resale, for stockpiling, or to generate commission;
(f) send unsolicited bulk email, SMS or messages, post to forums, comment sections or groups
    contrary to their rules, or use any method that would reasonably be regarded as spam;
(g) use cookie stuffing, forced clicks, pop-unders, adware, browser extensions, toolbars,
    incentivised clicks, bot traffic or any other method of generating clicks or orders that a
    customer did not choose;
(h) make any statement about the Products that is false, exaggerated or likely to mislead,
    including about price, availability, results, ingredients or comparisons with another
    product;
(i) promote the Products alongside adult, illegal, hateful, violent or otherwise disreputable
    content; or
(j) promote the Products to audiences outside [SHIPPING TERRITORY] in a way that generates orders
    Brand cannot fulfil.
5.2 A breach of Section 5.1 or Section 6 entitles Brand to terminate immediately under Section
11.2 and to reverse commission on affected orders.

6. CLAIMS
6.1 Affiliate may make only the claims about the Products that appear on [APPROVED CLAIMS LIST]
and on the Products' current labelling, in substantially the form given. Anything outside that
list requires Brand's prior written approval.
6.2 Without limiting Section 6.1, Affiliate shall not state or imply that a Product diagnoses,
treats, cures, prevents or reduces the risk of any disease or condition; shall not make any
weight-loss, performance, medical or health-outcome claim that is not on the approved list; shall
not state or imply that a Product is suitable for children, for pregnancy or nursing, or for a
person with a medical condition; and shall not describe a Product's ingredients or benefits in
terms the approved list does not use.
6.3 Affiliate shall not present a personal experience, testimonial or review in a way that
attributes to the Products a result the approved list does not support.
6.4 What may be said about a product of this kind depends on the product, the channel and the
jurisdiction, and changes over time. The approved list is set by Brand with its counsel, and
Affiliate shall use the current version Brand supplies. Where Affiliate is unsure whether a
statement is within the list, it shall ask Brand before publishing.

7. DISCLOSURE
7.1 Affiliate shall clearly and conspicuously disclose its paid and incentivised relationship with
Brand in every promotion of the Products, in the placement and wording the Program Policies
specify for the channel in question.
7.2 Disclosure expectations for paid promotion vary by channel and jurisdiction and change over
time. Brand sets the required form of disclosure with its counsel and states it in the Program
Policies; Affiliate shall use the current form and shall not substitute its own.
7.3 Affiliate shall not remove, obscure, truncate or place below a fold or a "more" link any
disclosure required by Section 7.1.

8. TRADEMARKS AND ASSETS
8.1 Brand grants Affiliate a non-exclusive, non-transferable, revocable, royalty-free licence to
use [ASSET KIT] and Brand's marks solely to promote the Products on the approved channels during
the Term, in the form Brand supplies.
8.2 Affiliate shall not alter Brand's marks, logos, packaging images or product photography;
shall not use superseded packaging or discontinued product images; shall not create derivative
logos or lockups; and shall not use Brand's marks in a way that suggests Affiliate is Brand, is
endorsed beyond the affiliate relationship, or speaks for Brand.
8.3 Affiliate shall not register or apply to register Brand's marks or anything confusingly
similar anywhere, and shall not challenge Brand's ownership of them. Goodwill from Affiliate's
use runs to Brand.
8.4 Content Affiliate creates remains Affiliate's, except that Affiliate grants Brand a
non-exclusive, royalty-free licence to reshare and repost Affiliate's Product-related content on
Brand's own channels during the Term, with attribution where the channel allows it.

9. REFUNDS, CHARGEBACKS AND REVERSAL
9.1 Commission is held for [HOLDING PERIOD] before it becomes payable.
9.2 Where an order is refunded, cancelled, returned, charged back, disputed, or found to be
fraudulent, duplicated or placed in breach of this Agreement, the commission on that order is
reversed in full, whether or not it has been paid.
9.3 A reversal may be applied within [REVERSAL WINDOW] of the order date. Brand shall set off a
reversal against commission next payable, and where no commission is payable within that window,
Affiliate shall repay the amount on request.
9.4 Brand shall provide a commission statement each [PAYMENT CYCLE] showing orders, the Commission
Base, the rate, reversals and the amount payable. Affiliate may dispute a statement within
[STATEMENT DISPUTE PERIOD], failing which it is final.

10. PAYMENT
10.1 Commission that has cleared the Holding Period and has not been reversed is paid
[PAYMENT CYCLE], to the payment details Affiliate provides.
10.2 Brand pays only when the balance owing reaches [MINIMUM PAYOUT]. A balance below the
threshold carries forward to the next cycle.
10.3 Brand may withhold payment while it investigates suspected fraud, prohibited marketing or a
breach of Sections 5, 6 or 7, and shall tell Affiliate that it is doing so and why.
10.4 Affiliate is responsible for its own taxes on commission, and shall provide the tax
documentation Brand reasonably requires before payment is made.

11. TERM AND TERMINATION
11.1 This Agreement begins on the Effective Date and continues until terminated. Either party may
terminate for convenience on [TERMINATION NOTICE] written notice, which for this purpose includes
notice through the affiliate platform.
11.2 Brand may terminate immediately on written notice if Affiliate breaches Section 5, 6, 7 or 8,
if Affiliate's content or conduct is reasonably likely to damage Brand's reputation, or if
Affiliate becomes insolvent. For any other breach, Brand may terminate if Affiliate has not cured
within [CURE PERIOD] of written notice.
11.3 On termination Brand shall deactivate Affiliate's links and codes, and no commission is
payable on an order placed after the effective time of termination, whatever link or code it
carries.
11.4 On termination Affiliate shall immediately cease all use of Brand's marks, assets and
claims, and shall within [ASSET REMOVAL PERIOD] remove from every channel it controls all Brand
logos, product images, tracking links, codes and any statement implying a continuing relationship
with Brand. Affiliate shall confirm in writing when removal is complete.
11.5 Commission earned on Qualifying Sales placed before termination is paid on the normal cycle,
subject to the Holding Period and to reversal under Section 9, except where Brand terminated under
Section 11.2, in which case Brand may withhold commission on orders affected by the breach.

12. CONFIDENTIALITY AND DATA
12.1 Affiliate shall keep confidential Brand's non-public information, including commission
structures, conversion data, unreleased products, pricing plans and customer information; shall
use it only for the Program; and shall not disclose it. These obligations continue for
[CONFIDENTIALITY YEARS] years after termination.
12.2 Affiliate shall not collect, store or use personal information of Brand's customers obtained
through the Program other than as the Program Policies permit, and shall not represent that it
handles orders, payments, returns or customer service for Brand.
12.3 Each party shall keep records relating to the Program for [RECORD RETENTION PERIOD] and shall
make them available to the other on reasonable request to verify commission or compliance.

13. INDEMNITY AND LIABILITY
13.1 Affiliate shall defend and indemnify Brand and its officers, employees and agents against
third-party claims, and the resulting losses, damages, costs and reasonable legal fees, arising
from Affiliate's content, statements or claims about the Products, from Affiliate's marketing
methods, from Affiliate's breach of this Agreement, or from Affiliate's use of third-party
material.
13.2 Brand shall defend and indemnify Affiliate against third-party claims arising from a defect
in a Product as supplied by Brand or from the Products' labelling as supplied by Brand.
13.3 The party seeking indemnity shall give prompt written notice, allow the indemnifying party to
control the defence with counsel it selects, and cooperate at that party's expense.
13.4 Brand does not guarantee any level of traffic, conversion, order volume or commission, and
neither party is liable to the other for lost profits or anticipated commission.

14. GENERAL
14.1 Affiliate shall not assign this Agreement or subcontract its participation in the Program
without Brand's prior written consent. Brand may assign to an affiliate or to a successor in a
merger or a sale of substantially all of its assets or of the [BRAND NAME] brand.
14.2 Where Affiliate participates through an affiliate network, this Agreement governs the
relationship between Brand and Affiliate, and Affiliate shall tell Brand promptly of any network
term that conflicts with it.
14.3 Notices must be in writing and are effective on delivery to the addresses above or through
the affiliate platform. This Agreement, with the Program Policies and any exhibit, is the entire
agreement between the parties on its subject matter. A failure to enforce a provision is not a
waiver of it, and if any provision is held unenforceable the remainder continues in effect. This
Agreement may be signed or accepted electronically.
14.4 Sections 5, 6, 8.3, 9, 11.3 through 11.5, 12, 13 and this Section 14 survive termination.
14.5 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
to its conflict of laws principles, and the parties consent to venue in [VENUE].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [AFFILIATE NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
EXHIBIT A — APPROVED CLAIMS LIST
─────────────────────────────────────────────────────────────────────────────

Have counsel set this list before the Program opens, and re-date it whenever a formulation, label
or channel changes. Affiliates may use only what appears here.

Version and date ............................ __________________________________
Products covered ............................ __________________________________
Approved product descriptions ............... __________________________________
Approved ingredient statements .............. __________________________________
Approved benefit or experience language ..... __________________________________
Approved comparisons, if any ................ __________________________________
Language that may never be used ............. __________________________________
Required disclosure wording, by channel ..... __________________________________
Who to ask when a statement is not on the list __________________________________


─────────────────────────────────────────────────────────────────────────────
EXHIBIT B — COMMISSION AND ORDER ECONOMICS
─────────────────────────────────────────────────────────────────────────────

Complete this before setting the rate.

Representative order used ................... __________________________________
Order value before discount ................. __________________________________
Affiliate code discount ..................... __________________________________
Order value after discount .................. __________________________________
Commission at the stated rate ............... __________________________________
Shipping and packaging cost ................. __________________________________
Payment processing cost ..................... __________________________________
Landed cost of goods ........................ __________________________________
CONTRIBUTION AFTER COMMISSION, THIS ORDER ... __________________________________
`,
  },
];

export default PARTNERSHIP_TEMPLATES;
